import { useState, useEffect } from 'react';
import { Zap, Filter } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import { usePage } from '../contexts/PageContext.jsx';

const PRIORITY_COLORS = { 1: '#6b7280', 2: '#3b82f6', 3: '#f59e0b', 4: '#ef4444' };
const PRIORITY_LABELS = { 1: 'L1 background', 2: 'L2 watch', 3: 'L3 trigger', 4: 'L4 emergency' };

export default function SignalQueuePage() {
  const { setPage } = usePage();
  useEffect(() => { setPage('signal-queue'); }, []);

  const [signals, setSignals] = useState([]);
  const [filter, setFilter] = useState(0); // 0 = all, 1-4 = priority levels

  useEffect(() => {
    async function load() {
      try {
        const r = await fetch('/api/kairos/signal-queue?limit=200');
        if (r.ok) setSignals(await r.json());
      } catch {}
    }
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);

  const filtered = filter === 0 ? signals : signals.filter(s => s.priority_level === filter);
  const stats = {
    l4: signals.filter(s => s.priority_level === 4).length,
    l3: signals.filter(s => s.priority_level === 3).length,
    l2: signals.filter(s => s.priority_level === 2).length,
    l1: signals.filter(s => s.priority_level === 1).length,
    triggered: signals.filter(s => s.triggered_cycle_id).length,
  };

  async function fireTest() {
    try {
      const r = await fetch('/api/kairos/debug/fire-test-signal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: 'Test emergency: Iran oil facility strike reported', assets: ['Oil'], sentiment: 'bullish' }),
      });
      if (r.ok) setTimeout(() => window.location.reload(), 1000);
    } catch {}
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Zap className="w-6 h-6 text-korinth-accent" />
        <h1 className="text-2xl font-bold text-white">Signal Queue</h1>
        <span className="text-korinth-muted text-sm">Priority-scored signals · admin/debug</span>
        <button onClick={fireTest} className="ml-auto px-3 py-1.5 rounded text-[10px] font-mono bg-korinth-danger/10 border border-korinth-danger/30 text-korinth-danger hover:bg-korinth-danger/20">
          Fire Test L3 Signal
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-2">
        {[['L4', stats.l4, '#ef4444'], ['L3', stats.l3, '#f59e0b'], ['L2', stats.l2, '#3b82f6'], ['L1', stats.l1, '#6b7280'], ['Triggered', stats.triggered, '#10b981']].map(([lbl, val, color]) => (
          <div key={lbl} className="bg-korinth-bg rounded px-3 py-2 text-center border border-korinth-border/30">
            <div className="text-lg font-mono font-bold" style={{ color }}>{val}</div>
            <div className="text-[9px] text-korinth-muted">{lbl}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-1">
        <Filter className="w-3 h-3 text-korinth-muted" />
        {[0, 4, 3, 2, 1].map(lvl => (
          <button key={lvl} onClick={() => setFilter(lvl)}
            className={`px-2 py-1 rounded text-[10px] font-mono ${filter === lvl ? 'bg-korinth-accent/10 text-korinth-accent border border-korinth-accent' : 'text-korinth-muted border border-transparent hover:bg-korinth-border/50'}`}>
            {lvl === 0 ? 'All' : PRIORITY_LABELS[lvl]}
          </button>
        ))}
      </div>

      <ModuleContainer title={`Queue (${filtered.length})`} subtitle="Priority · source · time · what happened · outcome">
        {filtered.length > 0 ? (
          <div className="space-y-1 max-h-[600px] overflow-y-auto">
            {filtered.map(s => {
              const color = PRIORITY_COLORS[s.priority_level] || '#6b7280';
              const assets = s.affected_assets ? (() => { try { return JSON.parse(s.affected_assets); } catch { return []; } })() : [];
              const timeAgoMin = s.scored_at ? Math.floor((Date.now() - new Date(s.scored_at).getTime()) / 60000) : null;
              const timeTxt = timeAgoMin == null ? '—' : timeAgoMin < 1 ? 'just now' : timeAgoMin < 60 ? `${timeAgoMin}m ago` : `${Math.floor(timeAgoMin / 60)}h ago`;
              return (
                <div key={s.id} className="bg-korinth-bg rounded border-l-2 px-3 py-2" style={{ borderLeftColor: color }}>
                  {/* Top row: priority, source, time, outcome */}
                  <div className="flex items-center gap-2 text-[10px] font-mono flex-wrap">
                    <span className="font-bold px-1.5 py-0.5 rounded" style={{ color, backgroundColor: color + '15' }}>L{s.priority_level}</span>
                    <span className="text-[9px] uppercase text-korinth-muted">{s.signal_type}</span>
                    <span className="text-white font-bold">{s.source_name || '—'}</span>
                    <span className="text-korinth-muted">· {timeTxt}</span>
                    {assets.length > 0 && (
                      <div className="flex gap-1">
                        {assets.slice(0, 3).map(a => (
                          <span key={a} className="text-[8px] font-bold px-1 py-0.5 rounded bg-korinth-border text-korinth-text">{a}</span>
                        ))}
                      </div>
                    )}
                    <span className="ml-auto flex items-center gap-1.5">
                      {s.triggered_cycle_id
                        ? <span className="text-[8px] px-1 py-0.5 rounded bg-korinth-accent/10 text-korinth-accent font-bold">→ cycle #{s.triggered_cycle_id}</span>
                        : s.priority_level >= 3 && !s.processed
                          ? <span className="text-[8px] px-1 py-0.5 rounded bg-korinth-warning/10 text-korinth-warning">pending cycle</span>
                          : <span className="text-[8px] text-korinth-muted">no cycle</span>
                      }
                      {s.processed ? <span className="text-[8px] text-korinth-muted">✓ processed</span> : <span className="text-[8px] text-korinth-warning">pending</span>}
                    </span>
                  </div>
                  {/* Signal text — WHAT happened */}
                  {s.signal_text && (
                    <div className="text-[10px] font-mono text-korinth-text mt-1 leading-relaxed">
                      {s.signal_text.slice(0, 250)}
                    </div>
                  )}
                  {/* Priority reasoning — WHY this priority */}
                  {s.priority_reasoning && s.priority_reasoning !== 'routine' && (
                    <div className="text-[9px] font-mono text-korinth-muted italic mt-0.5">
                      Scoring: {s.priority_reasoning}
                    </div>
                  )}
                  {/* Outcome summary if available */}
                  {s.outcome_summary && (
                    <div className="text-[9px] font-mono text-korinth-accent mt-0.5">
                      → {s.outcome_summary}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : <p className="text-[10px] text-korinth-muted font-mono text-center py-6">No signals in queue</p>}
      </ModuleContainer>
    </div>
  );
}
